import {Button, ButtonSize, ButtonStyle} from '@mintdata-wsp/common/components/button/Button';
import {Form, FormFieldValidationResult} from '@mintdata-wsp/common/components/form/Form';
import {ErrorPopupSide, IFormFieldState} from '@mintdata-wsp/common/components/formField/FormField';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {
    IInputProps,
    IInputState,
    Input,
    InputEditModeResetHandler,
    InputSize
} from '@mintdata-wsp/common/components/input/Input';
import {Spinner, SpinnerSize, SpinnerStyle} from '@mintdata-wsp/common/components/spinner/Spinner';
import {PublishedApplicationResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PublishedApplicationResponse';
import {PublishedDesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PublishedDesignResponse';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {DesignRef, PublishMode} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Dialog} from '@mintdata-wsp/wsp/ui/components/forms/Dialog';
import {DialogButton} from '@mintdata-wsp/wsp/ui/components/forms/DialogButton';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {
    ButtonSize as WspButtonSize,
    ButtonStyle as WspButtonStyle
} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {DotsMenu} from '@mintdata-wsp/wsp/ui/components/menus/DotsMenu';
import {LinkMenuItemRenderer} from '@mintdata-wsp/wsp/ui/components/menus/LinkMenuItemRenderer';
import {MenuItem} from '@mintdata-wsp/wsp/ui/components/menus/MenuItem';
import {TextRun} from '@mintdata-wsp/wsp/ui/components/text/TextRun';
import {AccessModeDropdown} from '@mintdata-wsp/wsp/ui/dialogs/publish/AccessModeDropdown';
import {ConnectDomainDialog} from '@mintdata-wsp/wsp/ui/dialogs/publish/ConnectDomainDialog';
import {IPublishConfirmUrlChangeDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/publish/PublishConfirmUrlChangeDialog';
// import {Invitor} from '@mintdata-wsp/wsp/ui/dialogs/publish/invite/Invitor';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('@mintdata-wsp/wsp/ui/dialogs/publish/PublishDialog.less');
const cx = classnames.bind(styles);

export enum PublishDialogKind
{
    Full = 'Full',
    AcessModeOnly = 'AcessModeOnly'
}

export interface IPublishDialogProps
{
    designRef:DesignRef;
    kind:PublishDialogKind;
}

interface INewUrlPart
{
    oldVersion:PublishedApplicationResponse;
    isChanged:boolean;
}

const PUBLISH_URL_FORM_KEY = 'publish-url';

@observer
export class PublishDialog extends React.Component<IPublishDialogProps, any>
{
    private _form:Form = new Form();
    @observable private _resetEditModeHandler:InputEditModeResetHandler = null;
    private _inputWrapperRef:HTMLDivElement = null;
    private _publishedLinkRef:HTMLAnchorElement = null;

    @bind
    private _onInputWrapperRef(elementRef:HTMLDivElement):void
    {
        this._inputWrapperRef = elementRef;
    }

    @bind
    private _onPublishedLinkRef(elementRef:HTMLAnchorElement):void
    {
        this._publishedLinkRef = elementRef;
    }

    @bind
    private _onCancelClicked():void
    {
        controllers.ui.publish.hidePublishDialog();
    }

    @bind
    private _onPublishClicked():void
    {
        const controller = controllers.ui.publish;
        const uiRequest = rootState.ui.publish.request;

        if( rootState.ui.publish.progress.isPublished )
        {
            controller.hidePublishDialog();
            return;
        }

        uiRequest.designRef = this.props.designRef;

        const {currentUrlPart} = rootState.ui.publish.request;
        const newUrlPart:INewUrlPart = this._checkNewUrlPart(currentUrlPart);

        if( newUrlPart.isChanged )
        {
            controllers.ui.publishedApps.unpublish(newUrlPart.oldVersion.id);
        }

        // noinspection JSIgnoredPromiseFromCall
        controller.publishOpenDesign(uiRequest);
    }

    @bind
    private _onOpenClicked():void
    {
        // const url = controllers.ui.publish.computeFullUrl();
        // const win = window.open(url, '_blank');
        // win.focus();

        const e = new MouseEvent('click', {shiftKey: true,});

        if( this._publishedLinkRef )
            this._publishedLinkRef.dispatchEvent(e);
    }

    @bind
    private _urlFormatter(value:string, props?:IInputProps, state?:IInputState, formFieldState?:IFormFieldState<string>):string
    {
        const controller = controllers.ui.publish;

        return state && state.isEditMode ? value : controller.computeFullUrl();
    }

    private _checkNewUrlPart = (newValue:string):INewUrlPart =>
    {
        const {designRef} = this.props;

        const design:PublishedDesignResponse = controllers.ui.mainPage.getPublishedDesignById(designRef.designId);

        const {isBeta} = rootState.ui.publish.request;

        const version:PublishedApplicationResponse = design && design.versions &&
            design.versions.filter((ver) => ver.beta === isBeta)[0];

        return {
            oldVersion: version,
            isChanged: version && version.urlPart !== newValue,
        };
    };

    @bind
    private _onUrlChange(newValue:string):void
    {
        const newUrlPart:INewUrlPart = this._checkNewUrlPart(newValue);

        if( newUrlPart.isChanged )
        {
            this._form.getField(PUBLISH_URL_FORM_KEY).reset();

            controllers.ui.dialogs.showDialog(
                Dialogs.PublishConfirmUrlChange, false,
                {
                    oldValue: newUrlPart.oldVersion.urlPart,
                    newValue,
                    onConfirm: () => controllers.ui.publish.setUrlPart(newValue)
                } as IPublishConfirmUrlChangeDialogProps
            );
        }
        else
        {
            controllers.ui.publish.setUrlPart(newValue);
        }
    }

    @bind
    private _validateUrl(newValue:string, state:IFormFieldState<string>):Promise<FormFieldValidationResult>
    {
        return controllers.ui.publish.validateUrlPart(newValue, state);
    }

    @bind
    private _onMainUrlClicked():void
    {
        controllers.ui.publish.setIsBeta(false);
    }

    @bind
    private _onBetaUrlClicked():void
    {
        controllers.ui.publish.setIsBeta(true);
    }

    @bind
    private _onCustomUrlClicked():void
    {
        controllers.ui.publish.showConnectDomainDialog();
    }

    @bind
    private _onInviteChanged(emails:Array<string>):void
    {

    }

    @action.bound
    private _onSetEditMode(resetEditMode:InputEditModeResetHandler):void
    {
        this._resetEditModeHandler = resetEditMode;
    }

    @action.bound
    private _onResetEditMode():void
    {
        this._resetEditModeHandler = null;
    }

    @bind
    private _onDialogMouseDown(event:React.MouseEvent<any>):void
    {
        if( this._resetEditModeHandler && this._inputWrapperRef && !DomUtils.isChild(event.target as any, this._inputWrapperRef) )
            this._resetEditModeHandler();
    }

    // private _getUrlLink(value:string, props?:IInputProps, state?:IInputState, formFieldState?:IFormFieldState<string>):JSX.Element
    // {
    //     return <a className={styles.urlLink} target="_blank" href={value}>{value}</a>;
    // }

    @bind
    private _getPublishedUrlLink(value:string):JSX.Element
    {
        return (
            <a
                className={styles.urlLink}
                target="_blank"
                href={value}
                onClick={(event) => this._publishedLinkClick(event, value)}
                ref={this._onPublishedLinkRef}
            >
                {value}
            </a>
        );
    }

    private _publishedLinkClick(event:React.MouseEvent<HTMLAnchorElement>, url:string)
    {
        if( event.shiftKey ) return;

        event.preventDefault();
        event.stopPropagation();

        const e = new MouseEvent('click', {shiftKey: true,});
        event.target.dispatchEvent(e);
    }

    private _getDownloadMenu():JSX.Element
    {
        if( !ConfigUtil.isPublishZipEnabled() )
            return null;

        const designId = this.props.designRef.designId;
        const authParam = encodeURIComponent(`Workspace ${rootState.userSession.authToken}`);

        const downloadLink = `//${WindowLocationUtil.getWspApiHostname()}/api/publish/standalone-zip/${designId}?Authorization=${authParam}`;
        // const downloadFullLink = `//${WindowLocationUtil.getWspApiHostname()}/api/publish/standalone-zip/${designId}?Authorization=${authParam}&full=true`;

        const menuItems = [
            MenuItem.full(downloadLink, 'Download Zip', null, null, () => null, null, LinkMenuItemRenderer)
            //, MenuItem.full(downloadFullLink, 'Download Zip with all bundles', null, null, () => null, null, LinkMenuItemRenderer)
        ];

        return <DotsMenu menuItems={menuItems}
                         className={cx('dotsMenu')}
        />;
    }

    private _getSpinner():JSX.Element
    {
        return <Spinner size={SpinnerSize.Medium_24} style={SpinnerStyle.White} className={styles.spinner} />;
    }

    private _renderCorporateSSO():JSX.Element
    {
        return <TextRun className={cx('corporateSSOText')}>
            Integration with your existing corporate identity system via LDAP, SAML, OAuth and other mechanisms.
            <br /><br />
            Please contact <span className={cx('ssoEmail')}>sso-integration@mintdata.com</span> to enable this
            integration
            for your organization.
        </TextRun>;
    }

    private _renderContent():JSX.Element
    {
        const {isBeta} = rootState.ui.publish.request;
        const urlPart = rootState.ui.publish.request.currentUrlPart;

        const publishController = controllers.ui.publish;
        const urlPrefix:string = publishController.computeUrlPrefix();
        const urlPostfix:string = publishController.computeUrlPostfix();

        return <Column>
            <div ref={this._onInputWrapperRef}>
                {urlPart && <Input
                    form={this._form}
                    formKey={PUBLISH_URL_FORM_KEY}
                    validationFn={this._validateUrl}
                    errorPopupSide={ErrorPopupSide.Bottom}
                    size={InputSize.ExtraLarge}
                    isClickForEdit={true}
                    isConfirmEdit={true}
                    initialValue={urlPart}
                    editBtnClassName={cx('urlInputEditBtn')}
                    formatter={this._urlFormatter}
                    editableModeRenderer={this._getPublishedUrlLink}
                    onChange={this._onUrlChange}
                    theme={styles}
                    textPrefix={urlPrefix}
                    textPostfix={urlPostfix}
                    isInitiallySelected={true}
                    onSetEditMode={this._onSetEditMode}
                    onResetEditMode={this._onResetEditMode}
                />
                }
                {!urlPart && <div className={cx('urlPartSpinner')}>
                    <Spinner size={SpinnerSize.Medium_16} style={SpinnerStyle.Blue} />
                </div>
                }
            </div>
            <Row className={this._resetEditModeHandler ? styles.hiddenRow : null}>
                <Button caption="Production URL"
                        className={cx('mainUrlButton', 'flatButton', {isActive: !isBeta})}
                        onClick={this._onMainUrlClicked}
                        style={ButtonStyle.SimpleSecondary}
                        size={ButtonSize.Small}
                />
                <Button caption="Staging URL"
                        className={cx('betaUrlButton', 'flatButton', {isActive: isBeta})}
                        onClick={this._onBetaUrlClicked}
                        style={ButtonStyle.SimpleSecondary}
                        size={ButtonSize.Small}
                />
                {/*TODO: temporarily disable CNAME functionality (see MAM-655, MAM-684)*/}
                {/*<Button caption="Custom URL..."*/}
                {/*className={cx('customUrlButton', 'flatButton')}*/}
                {/*onClick={this._onCustomUrlClicked}*/}
                {/*style={ButtonStyle.SimpleSecondary}*/}
                {/*size={ButtonSize.Small}*/}
                {/*/>*/}
            </Row>
            <ConnectDomainDialog />
            {/*<div className={styles.inviteUsersLabel}>*/}
            {/*Work better together. Add people to your application.*/}
            {/*</div>*/}
            {/*<Invitor onChange={this._onInviteChanged}/>*/}
        </Column>;
    }

    public render():JSX.Element
    {
        const {designRef, kind} = this.props;
        const designName = designRef.designName;
        const isFullDialog = kind !== PublishDialogKind.AcessModeOnly;

        const {urlPart, mode, isBeta} = rootState.ui.publish.request;
        const {
            isPublishInProgress,
            hadErrorDuringPublish,
            publishProgressMessage,
            isPublished
        } = rootState.ui.publish.progress;

        const title = kind === PublishDialogKind.Full
            ? `Publish ${designName}`
            : `Update Sharing for ${designName}`;

        const buttonCaption = kind === PublishDialogKind.Full
            ? isPublished ? 'Open Application' : ('Publish' + (isBeta ? ' Staging' : ''))
            : 'Save';

        return (
            <Dialog
                className={cx('publishDialog')}
                onBodyMouseDown={this._onDialogMouseDown}
                onClose={this._onCancelClicked}
                title={title}
            >
                <Column className={cx('contentColumn')}>
                    {/*<TextHeading className={cx('designName')}>{title}</TextHeading>*/}

                    <Row className={cx('accessRow')}>
                        <AccessModeDropdown />
                        {this._getDownloadMenu()}
                    </Row>

                    {mode === PublishMode.CorporateSSO ? this._renderCorporateSSO() : this._renderContent()}

                    {!isPublishInProgress && hadErrorDuringPublish && <Row className={styles.errorMessage}>
                        <MDIcon
                            name={MDIcons.common.Info}
                            size={MDIconSize.medium20}
                            className={styles.errorIcon}
                        />
                        <div>
                            {publishProgressMessage}
                        </div>
                    </Row>}

                    {!hadErrorDuringPublish && <Row className={styles.progressMessage}>
                        {publishProgressMessage && <MDIcon
                            name={MDIcons.common.Info}
                            size={MDIconSize.medium20}
                            className={styles.progressIcon}
                        />}
                        <div>
                            {publishProgressMessage || <span>&nbsp;</span>}
                        </div>
                    </Row>}

                    {
                        isPublished &&
                        <Row className={styles.publishSuccessContent}>
                            <Column className={styles.viewOnMobile}>
                                <Row>
                                    <div className={styles.circle}>
                                        <div className={styles.checkmark} />
                                    </div>
                                    <div className={styles.statusText}>Application published.</div>
                                </Row>
                                <Row>
                                    {this._getPublishedUrlLink(this._urlFormatter(urlPart))}
                                </Row>
                            </Column>
                            <Row className={styles.viewOnDesktop}>
                                <div className={styles.circle}>
                                    <div className={styles.checkmark} />
                                </div>
                                <Column>
                                    <div className={styles.statusText}>Application published.</div>
                                    {this._getPublishedUrlLink(this._urlFormatter(urlPart))}
                                </Column>
                            </Row>
                        </Row>
                    }
                </Column>

                <Row className={cx('actionRow')}>
                    <DialogButton
                        caption={isPublished ? 'Close' : 'Cancel'}
                        className={cx('cancelButton')}
                        onClick={this._onCancelClicked}
                        style={WspButtonStyle.SimpleSecondary}
                        size={WspButtonSize.Large}
                    />
                    {
                        !isPublished &&
                        <DialogButton
                            caption={buttonCaption}
                            icon={isPublishInProgress ? this._getSpinner() : null}
                            className={cx('publishButton')}
                            onClick={this._onPublishClicked}
                            style={WspButtonStyle.Raised}
                            size={WspButtonSize.Large}
                            isDisabled={isPublishInProgress || mode === PublishMode.CorporateSSO}
                        />
                    }
                    {
                        isPublished &&
                        <DialogButton
                            caption={buttonCaption}
                            className={cx('publishButton', {isPublished})}
                            onClick={this._onOpenClicked}
                            style={WspButtonStyle.Raised}
                            size={WspButtonSize.Large}
                        />
                    }
                </Row>

            </Dialog>
        );
    }
}
